import type { Project, VoiceEvent } from '../types';
import { audioCtx, resumeAudio } from './context';
import { createGraph, playVoice, syncGraph, type MixerGraph } from './graph';
import { eventsForRange, loopLength, trackAudible, type PlayMode } from './sequencer';

/**
 * Live transport.
 *
 * Classic lookahead scheduler: a timer wakes every LOOKAHEAD_MS and hands the
 * Web Audio clock every event that starts inside the next SCHEDULE_AHEAD
 * seconds. The UI never touches sample-accurate timing — it just reads
 * `beatNow()` on an animation frame.
 */

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD = 0.14;

export interface EngineListener {
  onBeat?: (beat: number) => void;
  onVoices?: (events: VoiceEvent[]) => void;
  onStateChange?: (playing: boolean) => void;
}

class Engine {
  private graph: MixerGraph | null = null;
  private timer: number | null = null;
  private project: Project | null = null;
  private mode: PlayMode = 'pattern';
  private startTime = 0;
  private startBeat = 0;
  private scheduledTo = 0;
  private loopBeats = 4;
  private playing = false;
  private listeners = new Set<EngineListener>();
  private metroTick = -1;
  private previewSource: AudioBufferSourceNode | null = null;
  private previewGain: GainNode | null = null;
  private previewStart = 0;
  private previewDuration = 0;

  ensureGraph(): MixerGraph {
    if (!this.graph) {
      const ctx = audioCtx();
      this.graph = createGraph(ctx, ctx.destination);
    }
    return this.graph;
  }

  subscribe(l: EngineListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /** Push the latest project snapshot; keeps mixer params live while playing. */
  update(project: Project): void {
    this.project = project;
    const g = this.ensureGraph();
    syncGraph(g, project, (t) => trackAudible(project, t));
    if (this.playing) this.loopBeats = loopLength(project, this.mode);
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get playMode(): PlayMode {
    return this.mode;
  }

  async start(project: Project, mode: PlayMode, fromBeat = 0): Promise<void> {
    await resumeAudio();
    this.project = project;
    this.mode = mode;
    this.update(project);
    const ctx = audioCtx();
    this.loopBeats = loopLength(project, mode);
    this.startBeat = fromBeat;
    this.startTime = ctx.currentTime + 0.06;
    this.scheduledTo = fromBeat;
    this.metroTick = Math.floor(fromBeat) - 1;
    this.playing = true;
    this.emitState();
    if (this.timer === null) {
      this.timer = window.setInterval(() => this.tick(), LOOKAHEAD_MS);
    }
  }

  stop(): void {
    this.playing = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.emitState();
    for (const l of this.listeners) l.onBeat?.(this.startBeat);
  }

  toggle(project: Project, mode: PlayMode, fromBeat = 0): void {
    if (this.playing) this.stop();
    else void this.start(project, mode, fromBeat);
  }

  /** Current transport position in beats, wrapped into the active loop. */
  beatNow(): number {
    if (!this.playing || !this.project) return this.startBeat;
    const spb = 60 / this.project.bpm;
    const elapsed = audioCtx().currentTime - this.startTime;
    const raw = this.startBeat + Math.max(0, elapsed) / spb;
    return this.loopBeats > 0 ? raw % this.loopBeats : raw;
  }

  /** Unwrapped position, used internally for scheduling. */
  private rawBeat(): number {
    if (!this.project) return 0;
    const spb = 60 / this.project.bpm;
    return this.startBeat + Math.max(0, audioCtx().currentTime - this.startTime) / spb;
  }

  private tick(): void {
    if (!this.playing || !this.project) return;
    const project = this.project;
    const spb = 60 / project.bpm;
    const ctx = audioCtx();
    const horizon = this.rawBeat() + SCHEDULE_AHEAD / spb;
    if (horizon <= this.scheduledTo) return;

    const from = this.scheduledTo;
    const to = horizon;
    const graph = this.ensureGraph();
    const all: VoiceEvent[] = [];

    // Walk the range in loop-sized chunks so a short loop can wrap more than
    // once inside a single lookahead window.
    let cursor = from;
    while (cursor < to) {
      const cycle = Math.floor(cursor / this.loopBeats);
      const cycleStart = cycle * this.loopBeats;
      const localFrom = cursor - cycleStart;
      const localTo = Math.min(to - cycleStart, this.loopBeats);
      const timeAtZero = this.startTime + (cycleStart - this.startBeat) * spb;
      const evs = eventsForRange(project, {
        mode: this.mode,
        from: localFrom,
        to: localTo,
        timeAtZero,
      });
      for (const e of evs) {
        if (e.time > ctx.currentTime - 0.01) {
          playVoice(graph, e);
          all.push(e);
        }
      }
      cursor = cycleStart + localTo;
      if (localTo >= this.loopBeats) cursor = cycleStart + this.loopBeats;
      if (localTo <= localFrom) break;
    }

    if (project.master.metronome) this.scheduleMetronome(from, to, spb);

    this.scheduledTo = to;
    if (all.length) for (const l of this.listeners) l.onVoices?.(all);
  }

  private scheduleMetronome(from: number, to: number, spb: number): void {
    const ctx = audioCtx();
    const graph = this.ensureGraph();
    const bpb = this.project?.beatsPerBar ?? 4;
    for (let b = Math.ceil(from); b < to; b++) {
      if (b <= this.metroTick) continue;
      this.metroTick = b;
      const t = this.startTime + (b - this.startBeat) * spb;
      if (t < ctx.currentTime) continue;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const downbeat = this.loopBeats > 0 ? Math.round(b) % bpb === 0 : false;
      osc.frequency.value = downbeat ? 1600 : 1000;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(downbeat ? 0.5 : 0.25, t + 0.001);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
      osc.connect(g).connect(graph.master);
      osc.start(t);
      osc.stop(t + 0.06);
    }
  }

  /** Fire a single slice immediately, for auditioning in the editor. */
  preview(ev: Omit<VoiceEvent, 'time' | 'trackId'> & { trackId?: string }): void {
    void resumeAudio();
    const graph = this.ensureGraph();
    const ctx = audioCtx();
    playVoice(graph, {
      ...ev,
      trackId: ev.trackId ?? '__preview',
      time: ctx.currentTime + 0.01,
    } as VoiceEvent);
  }

  /**
   * Loops a raw buffer straight to the master bus, outside the transport.
   * Used by the loop finder to audition a candidate before importing it.
   */
  async startPreviewLoop(buffer: AudioBuffer, gain = 0.9): Promise<void> {
    await resumeAudio();
    this.stopPreviewLoop();
    const ctx = audioCtx();
    const graph = this.ensureGraph();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(graph.master);
    src.start();
    this.previewSource = src;
    this.previewGain = g;
    this.previewStart = ctx.currentTime;
    this.previewDuration = buffer.duration;
  }

  stopPreviewLoop(): void {
    if (!this.previewSource) return;
    try {
      this.previewSource.stop();
      this.previewSource.disconnect();
      this.previewGain?.disconnect();
    } catch {
      /* already stopped */
    }
    this.previewSource = null;
    this.previewGain = null;
  }

  get previewLooping(): boolean {
    return this.previewSource !== null;
  }

  /** Position within the previewing loop, 0..1. */
  previewPhase(): number {
    if (!this.previewSource || this.previewDuration <= 0) return 0;
    const elapsed = audioCtx().currentTime - this.previewStart;
    return (elapsed % this.previewDuration) / this.previewDuration;
  }

  masterAnalyser(): AnalyserNode {
    return this.ensureGraph().analyser;
  }

  trackAnalyser(id: string): AnalyserNode | null {
    return this.graph?.tracks.get(id)?.analyser ?? null;
  }

  private emitState(): void {
    for (const l of this.listeners) l.onStateChange?.(this.playing);
  }
}

export const engine = new Engine();
